/**
 * CustomerDetailPage (B1.12) — smart wrapper around the Lovable-delivered
 * `<CustomerDetail>` primitive under `shared/ui/customer/*`.
 *
 * Responsibilities (all the non-pure work):
 *   - fetch the `CustomerProfile` via React Query (`useCustomerProfile`)
 *   - pick the right label set from the active i18n language (Lovable ships
 *     full `trCustomerLabels` / `enCustomerLabels` — we don't remap them to
 *     our namespace; a future swap can happen when we wire backend labels)
 *   - wire side-effects: back navigation, call/whatsapp/email links, note save
 *   - render Lovable's `<CustomerDetailSkeleton>` / `<CustomerDetailErrorState>`
 *     for the non-happy paths so the look-and-feel stays consistent
 *
 * The presentational component lives in `@/shared/ui/customer/CustomerDetail`
 * — never call anything outside that barrel from this file.
 *
 * @see PETOTA_BUSINESS_UX_BLUEPRINT §B1.12
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CustomerDetail,
  CustomerDetailErrorState,
  CustomerDetailSkeleton,
  enCustomerLabels,
  trCustomerLabels,
  type CustomerProfile as LovableCustomerProfile,
} from '@/shared/ui/customer';
import { QuickPetModal } from '@/components/customers/QuickPetModal';
import { useCustomerProfile, useUpdateCustomerNotes } from '@/features/customers';
import type { CustomerProfile as DomainCustomerProfile } from '@/features/customers/types';

function adaptProfile(profile: DomainCustomerProfile): LovableCustomerProfile {
  return profile as unknown as LovableCustomerProfile;
}

export function CustomerDetailPage(): JSX.Element {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation('customers');
  const { data: profile, isLoading, isError, refetch } = useCustomerProfile(customerId);
  const updateNotes = useUpdateCustomerNotes();
  const [quickPetOpen, setQuickPetOpen] = useState(false);

  const labels = useMemo(
    () => (i18n.language.startsWith('en') ? enCustomerLabels : trCustomerLabels),
    [i18n.language],
  );

  const goBack = useCallback(() => navigate('/customers'), [navigate]);

  const handleSaveNotes = useCallback(
    async (cid: string, notes: string) => {
      try {
        await updateNotes.mutateAsync({ customerId: cid, internalNotes: notes });
        toast.success(t('notes.saved'));
      } catch {
        toast.error(t('notes.saveError'));
      }
    },
    [updateNotes, t],
  );

  const handleNewAppointment = useCallback(() => {
    navigate('/calendar');
  }, [navigate]);

  const handleCall = useCallback((_id: string) => {
    if (!profile) return;
    const phone = profile.customer.phone.replace(/\s+/g, '');
    window.location.href = `tel:${phone}`;
  }, [profile]);

  const handleWhatsapp = useCallback((_id: string) => {
    if (!profile) return;
    const phone = profile.customer.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${phone}`, '_blank', 'noopener,noreferrer');
  }, [profile]);

  const handleEmail = useCallback((_id: string) => {
    if (!profile?.customer.email) return;
    window.location.href = `mailto:${profile.customer.email}`;
  }, [profile]);

  if (isLoading) {
    return <CustomerDetailSkeleton />;
  }

  if (isError) {
    return (
      <CustomerDetailErrorState
        variant="error"
        labels={labels}
        onBack={goBack}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  if (!profile) {
    return <CustomerDetailErrorState variant="not-found" labels={labels} onBack={goBack} />;
  }

  return (
    <>
      <CustomerDetail
        profile={adaptProfile(profile)}
        labels={labels}
        onBack={goBack}
        onNewAppointment={handleNewAppointment}
        onEditPet={(petId) => {
          if (petId === '__new__') setQuickPetOpen(true);
        }}
        onCallCustomer={handleCall}
        onWhatsapp={handleWhatsapp}
        onEmailCustomer={handleEmail}
        onSaveNotes={handleSaveNotes}
        isSavingNotes={updateNotes.isPending}
      />
      <QuickPetModal
        customerId={profile.customer.id}
        open={quickPetOpen}
        onClose={() => setQuickPetOpen(false)}
      />
    </>
  );
}
